"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SettingsImpl = void 0;
const Contract = require("@tableau/api-external-contract-js");
const api_internal_contract_js_1 = require("@tableau/api-internal-contract-js");
const api_shared_js_1 = require("@tableau/api-shared-js");
const TableauEvent_1 = require("../Events/TableauEvent");
const ServiceRegistryUtil_1 = require("../Services/ServiceRegistryUtil");
class SettingsChangedEvent extends TableauEvent_1.TableauEvent {
    constructor(_newSettings) {
        super(Contract.TableauEventType.SettingsChanged);
        this._newSettings = _newSettings;
    }
    get newSettings() {
        return this._newSettings;
    }
}
class SettingsImpl {
    constructor(settingsInfo) {
        // Since promises can't be introspected for state, keep a variable that
        // indicates a save is in progress, so that set/erase can't be called during a save.
        this._saveInProgress = false;
        this.initializeSettings(settingsInfo);
    }
    erase(key) {
        api_shared_js_1.ErrorHelpers.verifyParameter(key, 'key');
        // Only make a modification if we have the key already
        if (this._currentSettings[key]) {
            this.verifySettingsAreUnlocked();
            delete this._currentSettings[key];
            this._isModified = true;
        }
    }
    get(key) {
        api_shared_js_1.ErrorHelpers.verifyParameter(key, 'key');
        return this._currentSettings[key];
    }
    getAll() {
        // Returns a mutable copy of the settings
        return Object.assign({}, this._currentSettings);
    }
    get isModified() {
        return this._isModified;
    }
    saveAsync() {
        this.verifySettingsAreUnlocked();
        // Just resolve immediately if settings are unchanged
        if (!this._isModified) {
            return Promise.resolve(this._currentSettings);
        }
        this._saveInProgress = true;
        // Use the settings service to save settings to twb
        const settingsService = api_shared_js_1.ApiServiceRegistry.get(ServiceRegistryUtil_1.ExtensionsRegistryId).getService("SettingsService" /* ExtensionsServiceNames.SettingsService */);
        return settingsService.saveSettingsAsync(this._currentSettings).then((newSettings) => {
            this._saveInProgress = false;
            this._isModified = false;
            if (this._currentSettings === undefined) {
                this._currentSettings = newSettings;
            }
            else {
                Object.assign(this._currentSettings, newSettings);
            }
            return newSettings;
        }, (reason) => {
            this._saveInProgress = false;
            return Promise.reject(reason);
        });
    }
    set(key, value) {
        api_shared_js_1.ErrorHelpers.verifyStringParameter(key, 'key'); // Key shouldn't be an empty string.
        api_shared_js_1.ErrorHelpers.verifyParameter(value, 'value'); // Empty string value is allowed.
        this.verifySettingsAreUnlocked();
        this._currentSettings[key] = value;
        this._isModified = true;
    }
    /**
     * Initializes all events relevant to settings object.  This is only a settingsUpdate event currently.
     *
     * @returns {Array<SingleEventManager>} Collection of event managers to pass to an EventListenerManager.
     */
    initializeEvents() {
        const results = new Array();
        let notificationService;
        try {
            notificationService = api_shared_js_1.ApiServiceRegistry.get(ServiceRegistryUtil_1.ExtensionsRegistryId).getService("notification-service" /* ServiceNames.Notification */);
        }
        catch (e) {
            // If we don't have this service registered, just return
            return results;
        }
        const settingsChangedEvent = new api_shared_js_1.SingleEventManagerImpl(Contract.TableauEventType.SettingsChanged);
        notificationService.registerHandler(api_internal_contract_js_1.NotificationId.SettingsChanged, () => true, (event) => {
            var _a;
            this._currentSettings = (_a = event.newSettings) !== null && _a !== void 0 ? _a : {};
            settingsChangedEvent.triggerEvent(() => new SettingsChangedEvent(event.newSettings));
        });
        results.push(settingsChangedEvent);
        return results;
    }
    initializeSettings(settingsInfo) {
        api_shared_js_1.ErrorHelpers.verifyParameter(settingsInfo, 'settingsInfo');
        api_shared_js_1.ErrorHelpers.verifyParameter(settingsInfo.settingsValues, 'settingsInfo.SettingsValues');
        this._currentSettings = settingsInfo.settingsValues;
        // Reset the isModified flag
        this._isModified = false;
    }
    /**
     * This helper should be called before any local update to this.currentSettings.
     * Checks if a current save call is still in progress and throws an error if so.
     */
    verifySettingsAreUnlocked() {
        if (this._saveInProgress) {
            throw new api_shared_js_1.TableauError(Contract.ErrorCodes.SettingSaveInProgress, SettingsImpl.ASYNC_SAVE_IN_PROGRESS);
        }
    }
}
exports.SettingsImpl = SettingsImpl;
SettingsImpl.ASYNC_SAVE_IN_PROGRESS = 'Async Save is in progress, updating settings is not allowed.';
//# sourceMappingURL=SettingsImpl.js.map